import DOMPurify from "dompurify";
import * as Sentry from "@sentry/react";
import { heicTo } from "heic-to";
import { convert } from "html-to-text";
import lamejs from "lamejs";
import { marked } from "marked";
import moment from "moment";
import { PDFDocument, StandardFonts } from "pdf-lib";
import { toast } from "react-toastify";
import i18n from "../i18n";
import api from "../services/api";
import apiAdmin from "../services/apiAdmin";
import { resolvePublicAssetUrl } from "./publicAssetUrl";
import { resolveNativeRecordingUpdateId } from "./nativeRecordingIdentity";
import { clampUnits } from "./unitsClamp";
import { isNonBillableList, NOT_BILLED_SUFFIX, NOT_BILLED_OTHER, NOT_BILLED_PATIENT_SUPPLIED } from "./notBilled";

export const defaultDateFormat = "MM/DD/YYYY HH:mm";
export const defaultDateFormatNoTime = "MM/DD/YYYY";

// Returns true if val represents an affirmative yes/true value,
// regardless of whether it's stored as boolean true or string "true"/"yes".
export const isYes = (val) => val === true || val === "true" || (typeof val === "string" && val.toLowerCase() === "yes");

export const decamelCase = (str) => {
  return str?.replace(/([a-z])([A-Z])/g, "$1 $2")?.replace(/^./, function (s) {
    return s.toUpperCase();
  });
};

// Accept ISO strings, "MM/DD/YYYY[ HH:mm]" strings (the app's stored format), Date objects,
// moment objects, and numeric timestamps. Supplying explicit formats prevents moment from
// falling back to native Date() parsing on non-ISO strings (warn-and-unreliable in Safari/FF).
const DATE_INPUT_FORMATS = [moment.ISO_8601, "MM/DD/YYYY HH:mm", "MM/DD/YYYY"];

export const formatDate = (date, showTime) => {
  if (!date) return "";
  const m = typeof date === "string" ? moment(date, DATE_INPUT_FORMATS) : moment(date);
  return m.isValid() ? m.format(showTime ? defaultDateFormat : defaultDateFormatNoTime) : "";
};

// Compact card-only date (e.g. "5/17"). Year is intentionally dropped — used on
// kanban cards where vertical density matters and the year adds no information
// at-a-glance for in-progress orders.
export const formatDateNoYear = (date) => {
  if (!date) return "";
  const m = typeof date === "string" ? moment(date, DATE_INPUT_FORMATS) : moment(date);
  return m.isValid() ? m.format("M/D") : "";
};

export const formatDateInput = (date) => {
  if (!date) return "";
  const m = typeof date === "string" ? moment(date, DATE_INPUT_FORMATS) : moment(date);
  return m.isValid() ? m.format("YYYY-MM-DD[T]HH:mm") : "";
};

export const formatDateNoTimeInput = (date) => {
  if (!date) return "";
  const m = typeof date === "string" ? moment(date, DATE_INPUT_FORMATS) : moment(date);
  return m.isValid() ? m.format("YYYY-MM-DD") : "";
};

export const onPressEnter = (cb) => {
  return (e) => {
    if (e.key === "Enter") {
      cb();
    }
  };
};

export const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value);
};

export const formatNumber = (num, decimals = 0) => {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num);
};

export const capitalize = (string) => {
  if (!string) return "";
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const hasOwn = (object, key) => {
  if (!object) return false;
  return Object.prototype.hasOwnProperty.call(object, key);
};

// Resolves the active DME provider for an order.
// If the order has an explicit DME selected (orderDmeKey), refreshes it from the current dmeList
// by matching on companyName so that field changes (e.g. newly added NPI) are picked up.
// If no explicit selection, falls back to the isDefault entry, then the first with a companyName
// (unless allowFallback is false, e.g. for manufacturer orgs that manage their own DME).
export const resolveDme = (orderDmeKey, dmeList, allowFallback = true) => {
  if (orderDmeKey) {
    return (dmeList || []).find((d) => d.companyName === orderDmeKey.companyName) || orderDmeKey;
  }
  if (!allowFallback) return null;
  return (dmeList || []).find((d) => d.isDefault) || (dmeList || []).find((d) => d.companyName) || null;
};

// True when the order's provider-side DME selection is the "Use Manufacturer's DME" sentinel.
// Handles four historical shapes: never-set, empty-string sentinel, the literal string
// "default" (used in older demo/seed data), and a dmeList entry whose key carries
// `useManufacturerDME: true`.
export const isUseManufacturerDME = (order) => {
  const k = order?.dmeProvider?.key;
  if (k == null || k === "" || k === "default") return true;
  if (typeof k === "object" && k.useManufacturerDME) return true;
  return false;
};

// Single source of truth for the DME used to auto-populate documentation
// (IVR info, CMS-1500, order PDF / R-11 DME Billing Entity, packing slip, order QR).
// Precedence:
//   1. order.manufacturerDmeProvider.key — manufacturer's per-order pick wins
//   2. provider's dmeProvider resolved against their dmeList
// Returns null when the order is "Use Manufacturer's DME" but the manufacturer hasn't picked yet,
// so callers leave fields blank instead of writing the placeholder sentinel into documents.
export const resolveEffectiveDme = (order, providerDmeList) => {
  const mfgKey = order?.manufacturerDmeProvider?.key;
  // Defensive: reject if the manufacturer-pick is somehow the placeholder sentinel itself
  // (the dropdown filters it out, but if any older payload or future code path stores the
  // sentinel inside `manufacturerDmeProvider.key`, returning it would render an empty DME
  // section in every document instead of falling through to the provider DME).
  if (mfgKey && typeof mfgKey === "object" && !Array.isArray(mfgKey) && !mfgKey.useManufacturerDME) {
    return mfgKey;
  }
  if (isUseManufacturerDME(order)) return null;
  return resolveDme(order?.dmeProvider?.key, providerDmeList);
};

const isPlainObject = (value) => value != null && typeof value === "object" && !Array.isArray(value);

// "CODE - description" -> { code, description }. Splits on the FIRST dash, so a
// description may itself contain dashes and "J45.909-Asthma" (no spaces) still
// splits; a bare code gets an empty description, not a copy of itself.
const splitCodeDesc = (text) => {
  const [head, ...rest] = String(text).split("-");
  return { code: head.trim(), description: rest.join("-").trim() };
};

// A row from "CODE - description" text, or null when there is no code in it.
const rowFromText = (text) => {
  const row = splitCodeDesc(text);
  return row.code ? row : null;
};

// A code value as text. Finite numbers are real inputs (CPT codes come back as
// numbers from some answers); booleans and anything structured are not codes.
const codeText = (value) => {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return "";
};

// ```json ... ``` -> the inner text. Element answers occasionally fence their
// JSON and the runner stores the answer verbatim.
const stripCodeFence = (text) => {
  const match = /^```[A-Za-z0-9_-]*[ \t]*\r?\n?([\s\S]*?)\r?\n?[ \t]*```$/.exec(text);
  return match ? match[1].trim() : text;
};

// Structured values (code lists, Plan of Care) are occasionally stored as a
// STRING containing JSON: a batched element answer quotes the nested value, or
// fences it, and the runner saves it verbatim (observed 2026-10-05 on an ER
// note's inOfficeProcedures, which rendered as an empty Bedside Procedures
// section with a valid code inside). Strips a code fence and parses, tolerating
// one level of double encoding. Returns the parsed array/object, or null when
// the text is not JSON -- callers then treat it as prose / "no codes".
export const parseStructuredString = (value) => {
  let parsed = value;
  for (let depth = 0; depth < 2 && typeof parsed === "string"; depth++) {
    const trimmed = stripCodeFence(parsed.trim());
    if (!/^[[{"]/.test(trimmed)) return null;
    try {
      parsed = JSON.parse(trimmed);
    } catch {
      return null;
    }
  }
  return parsed != null && typeof parsed === "object" ? parsed : null;
};

// Every code list in the app (ICD-10 / CPT section renderers, Edit Codes
// modals, optimizer, Plan of Care alignment, pending-sync signature, DME check,
// Analytics) goes through here, so it must return [{ code, description, ... }]
// rows for every shape the backend has ever stored and never throw. One bad
// element is dropped, not fatal: a null entry, a structured code, or a null
// dict value used to throw and take the whole section down with "Error loading
// the content" (prod ER notes, 2026-10-05). Pure: the input is never mutated.
export const normalizList = (html) => {
  if (!html) return [];
  if (typeof html === "string") {
    const parsed = parseStructuredString(html);
    return parsed ? normalizList(parsed) : [];
  }
  if (typeof html !== "object") return [];
  // A single scored row handed over bare instead of inside a one-element array.
  if (isPlainObject(html) && "code" in html) return normalizList([html]);

  let list = [];
  if (Array.isArray(html)) {
    list = html.map((elem) => {
      if (elem == null) return null;
      if (typeof elem === "string") return rowFromText(elem);
      if (typeof elem === "number") return Number.isFinite(elem) ? { code: String(elem), description: "" } : null;
      if (!isPlainObject(elem)) return null;

      if ("code" in elem) {
        // Correct format: { code, description, ...scored fields }. A row that
        // declares a code but has no usable one is dropped, never rebuilt from
        // some other property.
        const code = codeText(elem.code);
        if (!code) return null;
        const description = elem.description == null ? undefined : codeText(elem.description);
        return { ...elem, code, description };
      }

      // Legacy office procedure: { "CODE - description": { ...fields } }. Only
      // when it looks like one -- an object value, or a "CODE - desc" key --
      // so { description: "x" } is not turned into a row coded "description".
      const desc = Object.keys(elem)[0];
      const fields = desc != null ? elem[desc] : undefined;
      if (!desc || !(isPlainObject(fields) || (fields == null && desc.includes("-")))) return null;
      const row = rowFromText(desc);
      return row ? { ...row, ...(isPlainObject(fields) ? fields : {}) } : null;
    });
  } else {
    // Dict keyed "CODE - description" (the ICD-10 element / reviewer shape).
    // Alternatives come back as rows too (from a nested dict or an array of
    // strings/objects) on the RETURNED row; the stored dict is left untouched.
    list = Object.keys(html).map((codeDesc) => {
      const row = rowFromText(codeDesc);
      if (!row) return null;
      const fields = isPlainObject(html[codeDesc]) ? html[codeDesc] : null;
      if (!fields) return row;
      const out = { ...row, ...fields };
      if (isPlainObject(fields.alternatives) || Array.isArray(fields.alternatives)) {
        out.alternatives = normalizList(fields.alternatives);
      }
      return out;
    });
  }

  return list.filter(Boolean);
};

// The non-billable list's voice-edit labels may end in " x<n>" or " x <n>" (the dictated
// dose, design B3); only that list parses it, so a description ending in "x40" elsewhere is
// untouched.
const UNITS_SUFFIX = /\s+x\s*(\d+)\s*$/i;

const rowFromNonBillableLabel = (label) => {
  const row = rowFromText(label);
  if (!row) return null;
  const match = UNITS_SUFFIX.exec(row.description);
  const units = (match && clampUnits(match[1])) || 1;
  const description = match ? row.description.slice(0, match.index).trim() : row.description;
  return { ...row, description, units, quantity: units };
};

// Merge the Edit Codes picker's confirmed "CODE - description" labels into the
// encounter's current list, whatever shape it is stored in (array, dict, JSON
// string). Dedupes case-insensitively against the codes already present and
// within the selection itself. Starting from the NORMALIZED current value is
// the point: the modal used to start from [] for a stored string, which lost
// every existing code the moment one more was added.
//
// `variable` is the context-variable name being edited: for the non-billable list the
// labels are parsed for a dictated " x<n>" dose and the result is stamped non-billable.
export const appendCodeSelections = (currentValue, labels, variable) => {
  const nonBillable = isNonBillableList(variable);
  const newList = normalizList(currentValue);
  const existingCodes = new Set(
    newList
      .map((item) =>
        String(item?.code || "")
          .trim()
          .toUpperCase()
      )
      .filter(Boolean)
  );
  for (const label of labels || []) {
    const text = String(label ?? "");
    const row = nonBillable ? rowFromNonBillableLabel(text) : rowFromText(text);
    if (!row) continue;
    const normCode = row.code.toUpperCase();
    if (existingCodes.has(normCode)) continue;
    existingCodes.add(normCode);
    newList.push(row);
  }
  return nonBillable ? stampNonBillable(newList) : newList;
};

// The FE writers to the non-billable list that route through here (the Edit Codes picker,
// reorder/delete, and the voice-edit append via appendCodeSelections) stamp every row
// not-billed; a row written by another path (e.g. a direct voice-edit callback) is not
// stamped here, so the slip refusal by list name (valueSource) is the backstop. Lives here
// rather than in notBilled.js because it needs normalizList and notBilled.js must stay
// dependency-free (utils.js imports it).
// billingSetBy is never added here: the backend reads it as a person's explicit choice.
export const stampNonBillable = (list) =>
  normalizList(list).map((row) => ({
    ...row,
    notBilled: true,
    notBilledReason: row.notBilledReason === NOT_BILLED_OTHER ? NOT_BILLED_OTHER : NOT_BILLED_PATIENT_SUPPLIED,
  }));

// The non-billable list shows and edits a units count on every row (a row with no stored
// units reads as 1, see displayUnits), so it always gets the units control.
export const hasModifier = (type, code) =>
  type === "inOfficeProcedures" ||
  isNonBillableList(type) ||
  (type === "cpt2codes" && !code?.endsWith("F") && !code?.startsWith("M"));

export const sortByCode = (a, b) => {
  const aHasMod = hasModifier("cpt2codes", a.code);
  const bHasMod = hasModifier("cpt2codes", b.code);
  if (aHasMod !== bHasMod) return aHasMod ? 1 : -1;
  return (a.code || "").localeCompare(b.code || "");
};

const cpt2GroupHeaders = [
  { prefix: "J", label: "Drugs and Biologicals Administered" },
  { prefix: "M", label: "Quality & Performance Measures" },
  { prefix: "P", label: "Pathology & Laboratory Services" },
];

// The non-billable list groups Q-code biosimilars with the J-code drugs.
const groupMatches = (group, code, type) => {
  if (typeof code !== "string") return false;
  if (group.prefix === "J" && isNonBillableList(type)) return /^[JQ]/.test(code);
  return code.startsWith(group.prefix);
};

export const getCpt2GroupHeader = (codes, index, type) => {
  const code = codes[index]?.code;
  const prevCode = index > 0 ? codes[index - 1]?.code : null;
  for (const group of cpt2GroupHeaders) {
    if (groupMatches(group, code, type) && !groupMatches(group, prevCode, type)) {
      return group.label;
    }
  }
  return null;
};

export const audit = (event, chatId) => {
  if (process.env.REACT_APP_ENABLE_AUDIT === "true") {
    let email = localStorage.getItem("user");
    apiAdmin.sendLog(event, "audit", email, chatId);
  }

  Sentry.logger.info(chatId + ": " + event, { log_source: "audit" });
};

export const downloadDocument = async (image, apiCall) => {
  try {
    if (!apiCall) {
      apiCall = api.getFile;
    }

    let res = await apiCall(image.fileName);

    if (res.data) {
      if (res.data.file?.content) {
        // Sanitize the base64 the same way the output branch below does: strip any
        // data-URL prefix, remove whitespace/newlines, and fix padding. Without this,
        // atob() throws "InvalidCharacterError" on otherwise-valid files, which bubbled
        // up as a null return and silently dropped the file from the packet zip.
        let base64Data = res.data.file.content.replace(/^data:[^;]+;base64,/, "");
        base64Data = base64Data.replace(/\s/g, "");
        base64Data += "=".repeat((4 - (base64Data.length % 4)) % 4);
        const binaryData = Uint8Array.from(atob(base64Data), (c) => c.charCodeAt(0));
        const type = res.data.file.type ?? "application/pdf";
        const blob = new Blob([binaryData], { type });
        const url = URL.createObjectURL(blob);

        // Return a File (a Blob subclass) so binary consumers like JSZip work — a plain
        // { content, type } object makes generateAsync throw "Can't read the data of ...".
        // Attach `.content` (sanitized base64) the same way the output branch below does,
        // because modal viewers (ModalViewUploadedDoc) read `data.content` to render,
        // download, and print. Keep the returned `type` as the raw value so callers that
        // branch on it (e.g. ChatInputTab's `res.type !== "application/pdf"`) are unchanged.
        const file = new File([blob], image.name || image.fileName || "file", { type });
        file.content = base64Data;

        return { url, name: image.name, type: res.data.file.type, file };
      } else if (res.data.output?.file) {
        let base64 = res.data.output?.file;
        // 1️⃣ Remove data URL prefix if present
        base64 = base64.replace(/^data:application\/pdf;base64,/, "");
        // 2️⃣ CRITICAL: remove all whitespace / newlines
        base64 = base64.replace(/\s/g, "");
        // 3️⃣ Fix missing padding (Base64 must be multiple of 4)
        base64 += "=".repeat((4 - (base64.length % 4)) % 4);
        // 4️⃣ Decode
        const binary = atob(base64);
        // Debug sanity check
        // console.log(binary.startsWith("%PDF")); // MUST be true
        // 5️⃣ Convert to bytes
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
          bytes[i] = binary.charCodeAt(i);
        }

        // 6️⃣ Create Blob + URL
        const blob = new Blob([bytes], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);

        // 7️⃣ Create File (no await needed)
        const file = new File([blob], "filename.pdf", {
          type: "application/pdf",
        });
        file.content = base64;

        return {
          url,
          name: image.name,
          type: "application/pdf",
          file,
        };
      }
    } else {
      throw new Error("No data");
    }
  } catch (e) {
    console.error(e);

    return null;
  }
};

// Pre-process: sanitize and flatten compact problem lists to br-separated text before conversion
const preprocessHtmlForCopy = (html) => {
  const preprocess = document.createElement("div");
  preprocess.innerHTML = DOMPurify.sanitize(html); // nosemgrep: insecure-document-method
  preprocess.querySelectorAll(".pl-compact").forEach((pl) => {
    const bodies = pl.querySelectorAll(".poc-item-body");
    if (bodies.length > 0) {
      pl.innerHTML = [...bodies].map((b) => b.textContent.trim()).join("<br>"); // nosemgrep: insecure-document-method
    }
  });
  return preprocess.innerHTML;
};

export const getCopyAllText = (html) => {
  const flattened = preprocessHtmlForCopy(html);
  let text = convertHTMLToText(flattened);
  text = text.replace(/====PLAN====\n+/g, "====PLAN====\n");
  text = text.replace(/\n{3,}/g, "\n\n");
  return text;
};

export const copy = (html) => {
  const flattened = preprocessHtmlForCopy(html);
  let text = convertHTMLToText(flattened);
  text = text.replace(/====PLAN====\n+/g, "====PLAN====\n");
  text = text.replace(/\n{3,}/g, "\n\n");
  const cleanedHtml = cleanHTML(flattened);

  try {
    navigator.clipboard.write([
      new ClipboardItem({
        "text/plain": new Blob([text], { type: "text/plain" }),
        "text/html": new Blob([cleanedHtml], { type: "text/html" }),
      }),
    ]);

    toast.info(i18n.t("common.toasts.copiedToClipboard"), { toastId: "copied-to-clipboard" });
  } catch (e) {
    console.error("Error copying to clipboard", e);
    toast.error(i18n.t("common.toasts.errorCopyingNotAuthorized"));
  }
};

export const cleanHTML = (html) => {
  const container = document.createElement("div");
  container.innerHTML = DOMPurify.sanitize(html); // nosemgrep: insecure-document-method

  // Spell-check squiggles and serious-dx highlights are in-app presentation only —
  // unwrap them here (the choke point for both the clipboard text/html flavor and
  // Magic Copy's EHR paste queue) so they never land in a chart paste. Same classes
  // stripHighlightSpans strips before persisting.
  container.querySelectorAll("span.med-misspell, span.serious-dx-mark").forEach((span) => {
    const parent = span.parentNode;
    while (span.firstChild) parent.insertBefore(span.firstChild, span);
    parent.removeChild(span);
  });

  // Small title
  const title = container.querySelectorAll("*");
  title.forEach((title) => (title.style.fontSize = "16px"));

  // UL
  const ul = container.querySelectorAll("ul");
  ul.forEach((ul) => (ul.style.marginTop = "0px"));

  // Remove all button elements
  const buttons = container.querySelectorAll("button");
  buttons.forEach((button) => button.remove());

  // Custom section
  const los = container.querySelector(".level-of-service-section");
  if (los) los.innerHTML = DOMPurify.sanitize(convertHTMLToText(los.outerHTML).replaceAll(/\n/g, "<br>")); // nosemgrep: insecure-document-method

  const qcode = container.querySelector(".add-q-code");
  if (qcode) qcode.innerHTML = DOMPurify.sanitize(convertHTMLToText(qcode.outerHTML).replaceAll(/\n/g, "<br>")); // nosemgrep: insecure-document-method

  let icd10 = container.querySelectorAll(".icd-10-item");
  icd10.forEach((icd10) => {
    icd10.innerHTML = DOMPurify.sanitize(convertHTMLToText(icd10.outerHTML).replaceAll(/\n/g, "<br>")); // nosemgrep: insecure-document-method
  });

  // Remove drag handles
  const dragHandles = container.querySelectorAll(".drag-handle");
  dragHandles.forEach((handle) => handle.remove());

  // The missing-CPT warning is a billing prompt for the provider, not chart content
  container.querySelectorAll(".qcode-missing-cpt").forEach((warning) => warning.remove());

  // The DME documentation check is a prompt for the provider, not chart content
  container.querySelectorAll(".dme-flag").forEach((flag) => flag.remove());

  // Billing-rule flags are an advisory for the provider, not chart content
  container.querySelectorAll(".billing-rule-flags").forEach((flag) => flag.remove());

  // The Plan of Care "Add assessment" picker/hint is UI, not chart content
  container.querySelectorAll(".poc-add-assessment, .poc-add-assessment-hint").forEach((el) => el.remove());

  // Unwrap poc items to plain HTML
  let pocBodies = container.querySelectorAll(".poc-item-body");
  pocBodies.forEach((body) => body.replaceWith(...body.childNodes));
  let pocWrappers = container.querySelectorAll(".poc-item, .poc-list");
  pocWrappers.forEach((el) => el.replaceWith(...el.childNodes));

  // Get the cleaned HTML
  const cleanedHtml = container.innerHTML;

  return cleanedHtml;
};

export const convertSectionToHTML = (html) => {
  if (Array.isArray(html)) {
    let result = "<ul>";
    for (let item of html) {
      let val = item;
      if (typeof item === "object") {
        if (item.code) {
          val = item.code + " - " + item.description;
          if (item.icd10 && Array.isArray(item.icd10) && item.icd10.length > 0) {
            val += "<br/>ICD10: " + item.icd10.map((c) => c.code).join(", ") + " <br/>";
          }
          if (item.cpt && Array.isArray(item.cpt) && item.cpt.length > 0) {
            for (let c of item.cpt) {
              val += "<br/>- CPT: " + c.code + " - " + c.description + "<br/>";
              if (c.icd10) val += "-- ICD10: " + c.icd10.map((c) => c.code).join(", ") + "<br/><br/>";
            }
          }
        } else {
          val = JSON.stringify(item, null, 2);
        }
      }
      result += "<li>" + val + "</li>";
    }
    result += "</ul>";
    return result;
  }
  if (typeof html !== "string") return JSON.stringify(html, null, 2);
  return html;
};

export const convertHTMLToText = (html) => {
  if (!html) return "";

  html = html.replace(/<i\b[^>]*>.*?<\/i>/gis, "");
  html = html.replace(/<br><\/li>/gis, "</li>");
  let text = convert(html, {
    wordwrap: false,
    formatters: {
      qCodesFormatter: function (elem, walk, builder, formatOptions) {
        builder.openBlock();
        let code = elem.children?.[0]?.children?.[0]?.data?.trim();
        let used = elem.children?.[1]?.children?.[0]?.data?.trim();
        let wasted = elem.children?.[2]?.children?.[0]?.data?.trim();
        builder.addLiteral(code + ":\n\t" + used + "2 \n\t" + wasted + "2\n");
        builder.closeBlock();
      },
      levelOfServiceFormatter: function (elem, walk, builder, formatOptions) {
        builder.openBlock();

        let annualCode = elem.children?.[0]?.children?.[1]?.attribs["data-value"];
        let enm = elem.children?.[1]?.children?.[1]?.attribs["data-value"];
        let modifier =
          elem.children?.[2]?.children?.[0]?.children?.[0]?.children?.[2]?.attribs["aria-checked"] === "true" && "25";
        if (elem.children?.[0]?.children?.[0]?.children?.[0]?.data === "E/M Code") {
          enm = annualCode;
          annualCode = "";
        }
        let text = "";
        if (annualCode) {
          text += `Annual Wellness Code: ${annualCode}\n`;
        }
        if (enm) {
          text += `E/M Code: ${enm}\n`;
        }
        if (modifier) {
          text += `Modifier: ${modifier}\n`;
        }
        builder.addLiteral(text);
        builder.closeBlock();
      },
      icd10Formatter: function (elem, walk, builder, formatOptions) {
        // Recursively collect ALL text under a node. Robust to markup nesting changes
        // (the old fixed `children[0].children[0].data` path silently returned undefined
        // when an entry's code was blank — React renders `{undefined}` as an EMPTY badge —
        // and printed the literal "- undefined -" into the EHR paste).
        const allText = (n) => (!n ? "" : n.type === "text" ? n.data || "" : (n.children || []).map(allText).join(""));
        // Depth-first descendant search. A direct-children scan misses the mobile
        // markup, where SwipeToDelete nests .icd-10-code/.icd-10-desc two wrappers
        // deep (.swipe-del > .swipe-del-fg) — every row was silently skipped and the
        // EHR paste showed the section heading with no codes. Rows never nest, so
        // the recursion cannot cross into another .icd-10-item.
        const findByClass = (n, cls) => {
          if (n?.attribs?.class?.includes(cls)) return n;
          for (const c of n?.children || []) {
            const hit = findByClass(c, cls);
            if (hit) return hit;
          }
          return null;
        };
        // Start at elem itself: .icd-10-item's own class never contains a searched
        // key, so this is equivalent to scanning children — but short-circuits at
        // the first hit instead of walking every sibling subtree via map().
        const byClass = (cls) => findByClass(elem, cls);
        const code = allText(byClass("icd-10-code")).trim();
        const multiplier = allText(byClass("icd-10-modifier")).trim();
        const desc = allText(byClass("icd-10-desc")).trim();
        const label = [multiplier, desc].filter(Boolean).join(" ");
        // Key off the badge's presence, not its text: the badge is translated, the chart is not.
        const notBilledNode = byClass("icd-10-not-billed");
        const suffix = notBilledNode ? NOT_BILLED_SUFFIX : "";
        // Never paste "undefined": when the code didn't resolve, keep the description
        // (so a diagnosis is never silently dropped) but omit the "<code> - " prefix.
        // Skip a fully-empty row entirely.
        if (!code && !label) return;
        builder.openBlock();
        builder.addLiteral("\t- " + (code ? code + " - " : "") + label + suffix);
        builder.closeBlock();
      },
      titleFormatter: function (elem, walk, builder, formatOptions) {
        let title = elem.children?.[0]?.children?.[0]?.data?.trim();
        if (!title && elem.children?.[0]?.attribs?.class?.includes("section-actions-label")) {
          title = elem.children?.[1]?.children?.[0]?.children?.[0]?.data?.trim();
        }
        if (title) {
          builder.addLineBreak();
          builder.addInline(title + ":");
        } else {
          builder.addLineBreak();
        }
      },
    },
    selectors: [
      // { selector: ".q-code", format: "qCodesFormatter" },
      { selector: ".add-q-code", format: "skip" },
      { selector: ".qcode-missing-cpt", format: "skip" },
      { selector: ".dme-flag", format: "skip" },
      { selector: ".billing-rule-flags", format: "skip" },
      { selector: ".poc-add-assessment", format: "skip" },
      { selector: ".poc-add-assessment-hint", format: "skip" },
      { selector: ".drag-handle", format: "skip" },
      { selector: "button", format: "skip" },
      { selector: ".level-of-service-section", format: "levelOfServiceFormatter" },
      { selector: ".icd-10-item", format: "icd10Formatter" },
      { selector: ".poc-item", format: "block", options: { leadingLineBreaks: 1, trailingLineBreaks: 1 } },
      { selector: ".poc-item-body", format: "block", options: { leadingLineBreaks: 0, trailingLineBreaks: 0 } },
      { selector: ".section-title", format: "titleFormatter" },
      { selector: "ul", options: { itemPrefix: "  - ", leadingLineBreaks: 0, trailingLineBreaks: 1 } },
      { selector: "h1", options: { trailingLineBreaks: true } },
      { selector: "p", options: { trailingLineBreaks: true, leadingLineBreaks: true } },
    ],
  });

  return text;
};

// Move an item within a list, returning a new array. Out-of-range or equal
// indices are a no-op (returns a shallow copy) rather than splicing `undefined`
// into the list — a list that shrinks mid-drag leaves react-dnd holding a stale
// index, and the old splice-based reorder corrupted the list with an undefined
// element that later crashed rendering (Sentry LUCASHEALTH-RA).
export const reorderList = (list, fromIndex, toIndex) => {
  if (!Array.isArray(list)) return list;
  const next = [...list];
  if (fromIndex < 0 || fromIndex >= next.length || toIndex < 0 || toIndex >= next.length || fromIndex === toIndex) {
    return next;
  }
  const [removed] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, removed);
  return next;
};

export const planOfCareToArray = (data) => {
  if (!data) return [];
  if (Array.isArray(data) && data.length > 0 && data[0]?.key !== undefined) {
    return data.map(normalizePocItem);
  }
  if (typeof data === "object" && !Array.isArray(data)) {
    return Object.keys(data).map((key) => normalizePocItem({ key, value: data[key] }));
  }
  if (typeof data !== "string") return null;

  // Backend has begun emitting Plan of Care as a JSON-encoded string of
  // [{"key": "...", "value": "...with \n and markdown..."}, ...] instead of
  // an actual array or HTML. JSON first (parseStructuredString also strips a
  // markdown code fence and tolerates double encoding, shared with the code
  // lists); anything that is not JSON is the legacy HTML form.
  const fromJson = tryParseStructuredPoc(data);
  if (fromJson) return fromJson;

  return parsePlanOfCareHTML(data);
};

const tryParseStructuredPoc = (raw) => {
  const parsed = parseStructuredString(raw);
  if (!parsed) return null;
  if (Array.isArray(parsed) && parsed.length > 0 && parsed[0]?.key !== undefined) {
    return parsed.map(normalizePocItem);
  }
  if (isPlainObject(parsed)) {
    return Object.keys(parsed).map((k) => normalizePocItem({ key: k, value: parsed[k] }));
  }
  return null;
};

const normalizePocItem = (item) => ({
  key: typeof item.key === "string" ? item.key : String(item.key ?? ""),
  value: normalizePocValueToHtml(item.value),
});

const POC_HTML_TAG_RE = /<\/?[a-z][\s\S]*?>/i;

const normalizePocValueToHtml = (value) => {
  if (value == null) return "";
  if (typeof value !== "string") return value;
  if (POC_HTML_TAG_RE.test(value)) return value;
  try {
    const out = marked.parse(value, { breaks: true, gfm: true, async: false });
    return typeof out === "string" ? out : value;
  } catch {
    return value;
  }
};

const POC_BLOCK_TAGS = new Set([
  "P",
  "DIV",
  "UL",
  "OL",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "TABLE",
  "BLOCKQUOTE",
  "PRE",
  "HR",
  "SECTION",
]);
const POC_HEADER_SEPARATOR_RE = /^\s*[:\-–]\s*/;

const isPocBlank = (node) => node?.nodeType === Node.TEXT_NODE && node.textContent.trim() === "";
const isPocBold = (node) =>
  node?.nodeType === Node.ELEMENT_NODE &&
  (node.tagName === "STRONG" || node.tagName === "B") &&
  node.textContent.trim().length > 0;
const isPocLineEnd = (node) =>
  node?.nodeType === Node.ELEMENT_NODE && (node.tagName === "BR" || POC_BLOCK_TAGS.has(node.tagName));
const isPocList = (node) => node?.nodeType === Node.ELEMENT_NODE && (node.tagName === "UL" || node.tagName === "OL");

const escapePocText = (text) => text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// Plain text of a node with a space at every line/block boundary, so list items
// and <br>-separated lines don't run together ("Labs reviewedBP ok").
const pocNodeText = (node) => {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent;
  if (node.nodeType !== Node.ELEMENT_NODE) return "";
  if (node.tagName === "BR") return " ";
  const inner = Array.from(node.childNodes).map(pocNodeText).join("");
  return POC_BLOCK_TAGS.has(node.tagName) || node.tagName === "LI" ? " " + inner + " " : inner;
};
const collapsePocText = (text) => text.replace(/\s+/g, " ").trim();

// A bold run is a problem header only when it ends its line or is followed by a
// ":" / "-" separator — a bolded drug or dose mid-sentence is plan content.
const endsPocHeaderLine = (bold) => {
  let next = bold.nextSibling;
  while (isPocBlank(next)) next = next.nextSibling;
  if (!next || isPocLineEnd(next)) return true;
  return next.nodeType === Node.TEXT_NODE && POC_HEADER_SEPARATOR_RE.test(next.textContent);
};

// The header <strong>/<b> that opens `el` (an <li> or block), or null.
const leadingPocHeader = (el) => {
  let first = el.firstChild;
  while (isPocBlank(first)) first = first.nextSibling;
  return isPocBold(first) && endsPocHeaderLine(first) ? first : null;
};

// {key, value} from an element opened by a header, without the separator.
const splitPocHeader = (el) => {
  const clone = el.cloneNode(true);
  const header = leadingPocHeader(clone);
  const key = header.textContent.trim();
  const next = header.nextSibling;
  header.remove();
  if (next?.nodeType === Node.TEXT_NODE) next.textContent = next.textContent.replace(POC_HEADER_SEPARATOR_RE, "");
  return { key, value: clone.innerHTML.trim() };
};

// Splits Plan of Care HTML into [{key, value}] problems. A header is bold text
// at the start of a line (top level, or opening a block / list item) that ends
// its line or carries a ":" / "-" separator; everything up to the next header is
// that problem's value, kept verbatim. Content with no header above it — a
// narrative, or bullets before the first problem — becomes value-less items
// keyed by its text, one per line or list item, so nothing is dropped.
const parsePlanOfCareHTML = (html) => {
  const container = document.createElement("div");
  container.innerHTML = DOMPurify.sanitize(html); // nosemgrep: insecure-document-method

  const items = [];
  let section = null; // { key, parts, stripSeparator }
  let orphanLine = []; // inline nodes before any header, on the current line

  const pushTextItem = (text) => {
    const key = collapsePocText(text);
    if (key) items.push({ key, value: "" });
  };
  const flushOrphanLine = () => {
    pushTextItem(orphanLine.map(pocNodeText).join(""));
    orphanLine = [];
  };
  const flushSection = () => {
    if (section) items.push({ key: section.key, value: section.parts.join("").trim() });
    section = null;
  };
  const startSection = (key, value) => {
    flushOrphanLine();
    flushSection();
    section = { key, parts: value ? [value] : [], stripSeparator: !value };
  };

  let atLineStart = true;
  for (const node of Array.from(container.childNodes)) {
    const isElement = node.nodeType === Node.ELEMENT_NODE;
    if (!isElement && node.nodeType !== Node.TEXT_NODE) continue;

    if (isPocBlank(node)) {
      if (section) section.parts.push(node.textContent);
      else orphanLine.push(node);
      continue;
    }

    if (atLineStart && isPocBold(node) && endsPocHeaderLine(node)) {
      startSection(node.textContent.trim());
      atLineStart = false;
      continue;
    }

    if (isElement && POC_BLOCK_TAGS.has(node.tagName)) {
      const header = isPocList(node) ? null : leadingPocHeader(node);
      if (header) {
        const { key, value } = splitPocHeader(node);
        startSection(key, value);
        section.stripSeparator = false;
      } else if (section) {
        section.parts.push(node.outerHTML);
        section.stripSeparator = false;
      } else if (isPocList(node)) {
        // A list before any header: each item is its own problem (when it opens
        // with a header) or its own value-less line.
        flushOrphanLine();
        for (const li of Array.from(node.children)) {
          if (li.tagName !== "LI") continue;
          if (leadingPocHeader(li)) items.push(splitPocHeader(li));
          else pushTextItem(pocNodeText(li));
        }
      } else {
        flushOrphanLine();
        pushTextItem(pocNodeText(node));
      }
      atLineStart = true;
      continue;
    }

    // Inline content: text, <br>, or an inline element.
    const isBreak = isElement && node.tagName === "BR";
    if (section) {
      let piece = isElement ? node.outerHTML : escapePocText(node.textContent);
      if (section.stripSeparator && !isElement) piece = piece.replace(POC_HEADER_SEPARATOR_RE, "");
      section.parts.push(piece);
      section.stripSeparator = false;
    } else if (isBreak) {
      flushOrphanLine();
    } else {
      orphanLine.push(node);
    }
    atLineStart = isBreak;
  }
  flushOrphanLine();
  flushSection();

  return items.length > 0 ? items : null;
};

const MEDICAL_SYNONYMS = {
  disc: ["disk", "discal", "intervertebral"],
  herniated: ["herniation", "displacement", "prolapse", "bulging"],
  back: ["lumbar", "lumbosacral", "spine", "spinal", "vertebral"],
  pain: ["discomfort", "ache", "painful", "algia"],
  chest: ["thoracic", "cardiac", "coronary", "pectoral", "angina"],
  head: ["cephalic", "cranial"],
  headache: ["cephalalgia", "migraine", "cephalgia"],
  diabetes: ["diabetic", "mellitus", "glycemic"],
  heart: ["cardiac", "coronary", "cardiovascular"],
  kidney: ["renal", "nephro", "nephrotic"],
  liver: ["hepatic"],
  lung: ["pulmonary", "respiratory"],
  blood: ["hematologic", "anemia"],
  cholesterol: ["lipid", "lipidemia", "hyperlipidemia"],
  pressure: ["tension", "hypertension", "hypertensive"],
  edema: ["swelling", "swollen", "oedema"],
  leg: ["extremity", "limb"],
  eye: ["ocular", "optic", "visual", "retinal", "orbital"],
  stomach: ["gastric", "gastro", "epigastric"],
  bone: ["osseous", "skeletal", "osteo"],
  joint: ["articular", "arthro", "arthritis"],
  nerve: ["neural", "neuro", "neuropathy"],
  skin: ["dermal", "dermatitis", "cutaneous"],
  infection: ["infectious", "septic", "sepsis"],
  fracture: ["broken", "break"],
  obese: ["obesity", "overweight"],
  anxiety: ["anxious"],
  depression: ["depressive", "depressed"],
};

const expandWord = (word) => {
  const expanded = [word];
  for (const key in MEDICAL_SYNONYMS) {
    if (word === key || MEDICAL_SYNONYMS[key].includes(word)) {
      expanded.push(key);
      expanded.push(...MEDICAL_SYNONYMS[key]);
    }
  }
  return expanded;
};

const medStem = (word) => word.replace(/(ation|tion|sion|ment|ness|ity|ous|ive|ing|ated|ized|ical|ed|er|ly)$/, "");

const diagnosisStopWords = new Set([
  "the",
  "a",
  "an",
  "of",
  "on",
  "in",
  "with",
  "and",
  "or",
  "to",
  "for",
  "acute",
  "chronic",
  "primary",
  "secondary",
  "bilateral",
  "unspecified",
  "initial",
  "subsequent",
  "sequela",
  "type",
  "other",
  "due",
  "not",
  "without",
  "history",
  "status",
  "current",
  "use",
  "long",
  "term",
]);

const diagnosisNormalize = (s) =>
  s
    .toLowerCase()
    .replace(/[,\-–().:/]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const diagnosisGetWords = (s) =>
  diagnosisNormalize(s)
    .split(" ")
    .filter((w) => w.length > 2 && !diagnosisStopWords.has(w));

export const matchDiagnosisScore = (a, b) => {
  if (!a || !b) return 0;

  const wordsA = diagnosisGetWords(a);
  const wordsB = diagnosisGetWords(b);
  if (wordsA.length === 0 || wordsB.length === 0) return 0;

  // Build expanded set for A (original + synonyms + stems)
  const expandedA = new Set();
  for (const w of wordsA) {
    expandWord(w).forEach((e) => expandedA.add(e));
    expandedA.add(medStem(w));
  }

  // Count matches from B against expanded A
  let matches = 0;
  for (const w of wordsB) {
    const variants = new Set([w, medStem(w), ...expandWord(w)]);
    for (const v of variants) {
      if (expandedA.has(v)) {
        matches++;
        break;
      }
    }
  }

  let score = matches / Math.min(wordsA.length, wordsB.length);

  // Penalize vague descriptors in b
  const bLower = b.toLowerCase();
  if (bLower.includes("other ") || bLower.includes("unspecified") || bLower.includes("not elsewhere")) {
    score *= 0.7;
  }

  return score;
};

export const pocContentPlain = (pocItem) => {
  if (pocItem.value && typeof pocItem.value === "string") {
    return pocItem.value
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }
  return "";
};

export const matchPocItem = (text, pocItem) => {
  const titleScore = matchDiagnosisScore(text, pocItem.key);
  if (titleScore >= 0.5) return titleScore;
  const content = pocContentPlain(pocItem);
  if (content) {
    const contentScore = matchDiagnosisScore(text, content) * 0.8;
    return Math.max(titleScore, contentScore);
  }
  return titleScore;
};

export const scorePocToIcd = (pocItem, icdPaired, useContent) => {
  let score = matchDiagnosisScore(pocItem.key, icdPaired.norm.description);
  if (useContent && score < 0.4) {
    const content = pocContentPlain(pocItem);
    if (content) score = Math.max(score, matchDiagnosisScore(content, icdPaired.norm.description) * 0.8);
  }
  return score;
};

// (icd, pocItem, useContent) matcher shared by the ICD-10 body (reorder/delete
// realignment) and the Plan of Care body (add assessment). Title first; the
// content fallback only applies when the title score is under the 0.4 threshold.
export const icdPocScore = (icd, poc, useContent) => {
  let score = matchDiagnosisScore(icd.description, poc.key);
  if (useContent && score < 0.4) {
    const content = pocContentPlain(poc);
    if (content) score = Math.max(score, matchDiagnosisScore(icd.description, content) * 0.8);
  }
  return score;
};

// ICD entries (in ICD order) left unpaired after 1:1 matching against the Plan of
// Care blocks: each block can claim at most one code, so a related sibling code is
// still offered. Unlike alignListToReference (ICD-order greedy), pairs are claimed
// strongest first, so a sibling can't take another code's exact block. Entries
// with neither code nor description are skipped; a code with no description is
// matched by its code, which is what the new block is titled with.
export const findIcdCodesWithoutAssessment = (icdList, pocArr) => {
  if (!Array.isArray(icdList)) return [];
  const blocks = Array.isArray(pocArr) ? pocArr.filter((b) => b && typeof b === "object") : [];
  const validIdx = [];
  icdList.forEach((icd, i) => {
    if (icd && typeof icd === "object" && (icd.description || icd.code)) validIdx.push(i);
  });
  const usedBlocks = new Set();
  const matched = new Set();
  const safeScore = (icd, poc, useContent) => {
    try {
      const s = icdPocScore(icd.description ? icd : { ...icd, description: icd.code }, poc, useContent);
      return Number.isFinite(s) ? s : 0;
    } catch {
      return 0;
    }
  };
  [false, true].forEach((useContent) => {
    // Score-ordered greedy: the strongest (code, block) pair claims first, so a
    // sibling code can't steal another code's exact block by ICD order.
    const pairs = [];
    validIdx.forEach((i) => {
      if (matched.has(i)) return;
      blocks.forEach((poc, b) => {
        if (usedBlocks.has(b)) return;
        const score = safeScore(icdList[i], poc, useContent);
        if (score >= 0.4) pairs.push({ i, b, score });
      });
    });
    pairs.sort((x, y) => y.score - x.score || x.i - y.i || x.b - y.b);
    pairs.forEach(({ i, b }) => {
      if (matched.has(i) || usedBlocks.has(b)) return;
      matched.add(i);
      usedBlocks.add(b);
    });
  });
  return validIdx.filter((i) => !matched.has(i)).map((i) => icdList[i]);
};

export const alignListToReference = (refItems, targetList, scoreFn) => {
  if (!refItems?.length || !targetList?.length) return null;

  const usedTarget = new Set();
  const matchedPairs = [];

  // Pass 1: high-confidence matches (title only)
  for (let r = 0; r < refItems.length; r++) {
    let best = { score: 0, idx: -1 };
    for (let t = 0; t < targetList.length; t++) {
      if (usedTarget.has(t)) continue;
      const score = scoreFn(refItems[r], targetList[t], false);
      if (score > best.score) best = { score, idx: t };
    }
    if (best.score >= 0.4 && best.idx >= 0) {
      matchedPairs.push({ refIdx: r, targetIdx: best.idx });
      usedTarget.add(best.idx);
    }
  }

  // Pass 2: content fallback for unmatched
  for (let r = 0; r < refItems.length; r++) {
    if (matchedPairs.some((m) => m.refIdx === r)) continue;
    let best = { score: 0, idx: -1 };
    for (let t = 0; t < targetList.length; t++) {
      if (usedTarget.has(t)) continue;
      const score = scoreFn(refItems[r], targetList[t], true);
      if (score > best.score) best = { score, idx: t };
    }
    if (best.score >= 0.4 && best.idx >= 0) {
      matchedPairs.push({ refIdx: r, targetIdx: best.idx });
      usedTarget.add(best.idx);
    }
  }

  if (matchedPairs.length === 0) return null;

  matchedPairs.sort((a, b) => a.refIdx - b.refIdx);

  const result = matchedPairs.map((m) => targetList[m.targetIdx]);
  for (let t = 0; t < targetList.length; t++) {
    if (!usedTarget.has(t)) result.push(targetList[t]);
  }

  return result;
};

// Given a deleted ICD-10 code, return the index in `pocArr` of the single
// assessment to remove, or -1 to remove nothing. Removes ONLY the deleted
// code's own assessment, and only when (a) it actually matches the deleted code
// (>= 0.4), (b) it is "orphaned" — no remaining code still claims it (>= 0.4),
// and (c) it is untouched — its title is NOT in `editedKeys`. This is
// the targeted replacement for the old "align-all + slice" drop, which could
// re-rank edited/unrelated assessments into the dropped tail.
// `scoreFn` is the same (icd, pocItem, useContent) matcher used for alignment.
export const findAssessmentToRemoveOnIcdDelete = (pocArr, deletedCode, remainingCodes, editedKeys, scoreFn) => {
  if (!Array.isArray(pocArr) || pocArr.length === 0 || !deletedCode || typeof scoreFn !== "function") return -1;

  let bestIdx = -1;
  let bestScore = 0;
  for (let t = 0; t < pocArr.length; t++) {
    const s = scoreFn(deletedCode, pocArr[t], true);
    if (s > bestScore) {
      bestScore = s;
      bestIdx = t;
    }
  }
  if (bestIdx < 0 || bestScore < 0.4) return -1; // deleted code had no assessment

  const target = pocArr[bestIdx];
  // Keep the assessment if ANY remaining code still has a real claim on it
  // (>= the 0.4 match threshold). We only remove assessments orphaned by THIS
  // deletion — never one another code still wants. (Stricter than ">= bestScore":
  // protects shared/ambiguous assessments a remaining code matches even weakly.)
  for (const code of remainingCodes || []) {
    if (scoreFn(code, target, true) >= 0.4) return -1;
  }

  const edited = editedKeys instanceof Set ? editedKeys : new Set(Array.isArray(editedKeys) ? editedKeys : []);
  if (edited.has(target.key)) return -1; // user edited/added it → keep (the exception)

  return bestIdx;
};

// Maintain the sidecar list of user-edited Plan of Care assessment titles.
// Returns a new array (never mutates). On a title rename, drops oldKey so the
// "edited" mark follows the assessment to its new title.
export const addEditedKey = (currentKeys, newKey, oldKey) => {
  const set = new Set(Array.isArray(currentKeys) ? currentKeys : []);
  if (oldKey && oldKey !== newKey) set.delete(oldKey);
  if (newKey) set.add(newKey);
  return Array.from(set);
};

export const syncProblemListOrder = (problemListHTML, pocItems) => {
  if (!problemListHTML || typeof problemListHTML !== "string" || !pocItems || pocItems.length === 0) return null;

  const container = document.createElement("div");
  container.innerHTML = DOMPurify.sanitize(problemListHTML); // nosemgrep: insecure-document-method
  const listItems = container.querySelectorAll("li");

  let lines;
  if (listItems.length > 0) {
    lines = [...listItems].map((li) => ({ text: li.textContent.trim(), html: li.outerHTML }));
  } else {
    lines = problemListHTML
      .split(/<br\s*\/?>|\n/)
      .filter((l) => l.trim())
      .map((l) => ({ text: l.replace(/<[^>]*>/g, "").trim(), html: l.trim() }));
  }

  if (lines.length === 0) return null;

  const aligned = alignListToReference(pocItems, lines, (poc, line, useContent) => {
    let score = matchDiagnosisScore(poc.key, line.text);
    if (useContent && score < 0.4) {
      const content = pocContentPlain(poc);
      if (content) score = Math.max(score, matchDiagnosisScore(content, line.text) * 0.8);
    }
    return score;
  });
  if (!aligned) return null;

  const reordered = aligned.map((l) => l.html);

  if (listItems.length > 0) {
    return "<ul>" + reordered.map((h) => (h.startsWith("<li") ? h : "<li>" + h + "</li>")).join("") + "</ul>";
  }
  return reordered.join("<br>");
};

export const sectionToHTML = (html) => {
  if (typeof html === "string") {
    return html;
  } else if (Array.isArray(html) && html.length > 0 && html[0]?.key !== undefined) {
    let result = "<ul>";
    for (let item of html) {
      if (!item.value && item.key) {
        result += `<li>${item.key}</li>`;
      } else {
        result += `<li><strong>${item.key}</strong>: `;
        if (typeof item.value === "string") {
          result += item.value;
        } else {
          result += objToHtml(item.value);
        }
        result += "</li>";
      }
    }
    result += "</ul>";
    return result;
  } else {
    return objToHtml(html);
  }
};

const objToHtml = (obj) => {
  let html = "";

  if (typeof obj === "string") {
    return html + `<li>${obj}</li>`;
  } else if (Array.isArray(obj)) {
    html += `<ul>`;
    for (let item of obj) {
      html += objToHtml(item);
    }
    return (html += `</ul>`);
  } else if (typeof obj === "object") {
    html += `<ul>`;
    for (let k in obj) {
      html += `<li><strong>${k}</strong>: `;
      if (typeof obj[k] === "string") {
        html += `${obj[k]}`;
      } else {
        html += objToHtml(obj[k]);
      }
      html += `</li>`;
    }

    return (html += `</ul>`);
  }
  return html;
};

// Moved to ./age so components needing only an age can import it without
// pulling in this module's heavy deps. Re-exported for existing callers.
export { computeAge, resolveUserAge } from "./age";

export const convertFileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const arrayBuffer = reader.result;
      // const bytes = new Uint8Array(arrayBuffer);

      // Detect MIME type from the file's content
      const mimeType = file.type || "application/octet-stream";

      // Convert ArrayBuffer to base64
      const base64 = arrayBufferToBase64(arrayBuffer);

      resolve(`data:${mimeType};base64,${base64}`);
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
};

export const arrayBufferToFileReader = (file) => {
  return new Promise((resolve, reject) => {
    let reader = new FileReader();

    reader.onload = function () {
      resolve(reader);
    };

    reader.readAsArrayBuffer(file);
  });
};

export const arrayBufferToDataUrl = (file) => {
  return new Promise((resolve, reject) => {
    let reader = new FileReader();

    reader.onload = function () {
      resolve(reader);
    };

    reader.readAsDataURL(file);
  });
};

export const getElemFromImg = (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = file.result;
    img.name = file.name;
    img.size = file.size;
    img.onload = function (el) {
      resolve(el.target);
    };
  });
};

const arrayBufferToBase64 = (buffer) => {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

export const convertBase64ToMP3 = async (base64Data) => {
  var blob = base64toBlob(base64Data, "audio/wav");
  const audioContext = new AudioContext();
  const fileReader = await openFileReader(blob);
  const arrayBuffer = fileReader.result;
  let audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
  var MP3Blob = await audioBufferToWav(audioBuffer);

  return MP3Blob;
};

export const combineArrayBuffers = (arrayBuffers) => {
  const totalLength = arrayBuffers.reduce((sum, arr) => sum + arr.byteLength, 0);
  console.log(`Total size: ${(totalLength / 1024 / 1024).toFixed(2)} MB`);

  const combined = new Uint8Array(totalLength);
  let offset = 0;

  for (const chunk of arrayBuffers) {
    combined.set(new Uint8Array(chunk), offset);
    offset += chunk.byteLength;
  }

  return combined;
};

export const convertArrayBuffersToMP3 = async (arrayBuffers) => {
  const combined = combineArrayBuffers(arrayBuffers);

  const audioContext = new AudioContext();

  console.log("Decoding audio data...");
  const audioBuffer = await audioContext.decodeAudioData(combined.buffer);

  console.log("Converting audio buffer to MP3...");
  const MP3Blob = await audioBufferToWav(audioBuffer);

  return MP3Blob;
};

// Convert array of ArrayBuffers to a data URL (for already-compressed audio)
// this is very fast since we don't decode/encode - just concatenate and base64
export const arrayBuffersToDataUrl = (arrayBuffers, mimeType) => {
  console.log(`Creating data URL for ${mimeType}...`);

  const combined = combineArrayBuffers(arrayBuffers);

  let binary = "";
  const chunkSize = 32768;

  for (let i = 0; i < combined.length; i += chunkSize) {
    const slice = combined.subarray(i, Math.min(i + chunkSize, combined.length));
    binary += String.fromCharCode.apply(null, slice);
  }

  const base64 = btoa(binary);
  const dataUrl = `data:${mimeType};base64,${base64}`;

  console.log(`Created data URL (${dataUrl.length} chars)`);

  return dataUrl;
};

export const processCompletedAudioTransfer = (recordings, transferId) => {
  const transfer = recordings[transferId];
  if (!transfer) return null;

  const { chunks, totalChunks, metadata } = transfer;

  if (Object.keys(chunks).length !== totalChunks) {
    console.warn(`Missing chunks for transfer ${transferId}: got ${Object.keys(chunks).length} of ${totalChunks}`);

    delete recordings[transferId];
    return null;
  }

  const buffers = [];

  for (let i = 0; i < totalChunks; i++) {
    buffers.push(chunks[i]);
  }

  console.log(`Reassembled audio for transfer ${transferId}: ${totalChunks} chunks`);

  delete recordings[transferId];

  return { buffers, metadata };
};

export const audioBufferToWav = async (aBuffer) => {
  let numOfChan = aBuffer.numberOfChannels,
    btwLength = aBuffer.length * numOfChan * 2 + 44,
    btwArrBuff = new ArrayBuffer(btwLength),
    btwView = new DataView(btwArrBuff),
    btwChnls = [],
    btwIndex,
    btwSample,
    btwOffset = 0,
    btwPos = 0;
  setUint32(0x46464952); // "RIFF"
  setUint32(btwLength - 8); // file length - 8
  setUint32(0x45564157); // "WAVE"
  setUint32(0x20746d66); // "fmt " chunk
  setUint32(16); // length = 16
  setUint16(1); // PCM (uncompressed)
  setUint16(numOfChan);
  setUint32(aBuffer.sampleRate);
  setUint32(aBuffer.sampleRate * 2 * numOfChan); // avg. bytes/sec
  setUint16(numOfChan * 2); // block-align
  setUint16(16); // 16-bit
  setUint32(0x61746164); // "data" - chunk
  setUint32(btwLength - btwPos - 4); // chunk length

  for (btwIndex = 0; btwIndex < aBuffer.numberOfChannels; btwIndex++) btwChnls.push(aBuffer.getChannelData(btwIndex));

  while (btwPos < btwLength) {
    for (btwIndex = 0; btwIndex < numOfChan; btwIndex++) {
      // interleave btwChnls
      btwSample = Math.max(-1, Math.min(1, btwChnls[btwIndex][btwOffset])); // clamp
      btwSample = (0.5 + btwSample < 0 ? btwSample * 32768 : btwSample * 32767) | 0; // scale to 16-bit signed int
      btwView.setInt16(btwPos, btwSample, true); // write 16-bit sample
      btwPos += 2;
    }
    btwOffset++; // next source sample
  }

  let wavHdr = lamejs.WavHeader.readHeader(new DataView(btwArrBuff));
  let wavSamples = new Int16Array(btwArrBuff, wavHdr.dataOffset, wavHdr.dataLen / 2);

  function setUint16(data) {
    btwView.setUint16(btwPos, data, true);
    btwPos += 2;
  }

  function setUint32(data) {
    btwView.setUint32(btwPos, data, true);
    btwPos += 4;
  }

  return await wavToMp3(wavHdr.channels, wavHdr.sampleRate, wavSamples);
};

async function wavToMp3(channels, sampleRate, samples) {
  var buffer = [];
  var mp3enc = new lamejs.Mp3Encoder(channels, sampleRate, 128);
  var samplesPerFrame = 1152;

  if (channels === 1) {
    var remaining = samples.length;
    for (var i = 0; remaining >= samplesPerFrame; i += samplesPerFrame) {
      var mono = samples.subarray(i, i + samplesPerFrame);
      var mp3buf = mp3enc.encodeBuffer(mono);
      if (mp3buf && mp3buf.length > 0) {
        buffer.push(new Int8Array(mp3buf));
      }
      remaining -= samplesPerFrame;
    }
  } else {
    var samplesPerChannel = Math.floor(samples.length / 2);
    var left = new Int16Array(samplesPerChannel);
    var right = new Int16Array(samplesPerChannel);

    for (var j = 0; j < samplesPerChannel; j++) {
      left[j] = samples[j * 2];
      right[j] = samples[j * 2 + 1];
    }

    var remainingStereo = samplesPerChannel;
    for (var k = 0; remainingStereo >= samplesPerFrame; k += samplesPerFrame) {
      var leftChunk = left.subarray(k, k + samplesPerFrame);
      var rightChunk = right.subarray(k, k + samplesPerFrame);
      var mp3bufStereo = mp3enc.encodeBuffer(leftChunk, rightChunk);
      if (mp3bufStereo && mp3bufStereo.length > 0) {
        buffer.push(new Int8Array(mp3bufStereo));
      }
      remainingStereo -= samplesPerFrame;
    }
  }

  var d = mp3enc.flush();
  if (d && d.length > 0) {
    buffer.push(new Int8Array(d));
  }

  var mp3Blob = new Blob(buffer, { type: "audio/mpeg" });
  return await blobToBase64(mp3Blob);
}

function blobToBase64(blob) {
  return new Promise((resolve, _) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
}

const base64toBlob = (b64Data, contentType = "", sliceSize = 512) => {
  const byteCharacters = atob(b64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: contentType });
  return blob;
};

export const base64ToSingleArrayBuffer = (b64Data) => {
  const binaryString = atob(b64Data);
  const bytes = new Uint8Array(binaryString.length);

  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return bytes.buffer;
};

const openFileReader = (blob) => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.onloadend = async () => {
      resolve(fileReader);
    };
    fileReader.readAsArrayBuffer(blob);
  });
};

export const globalSetCookie = async (key, value, expirationDays, domain = "lucashealth.ai") => {
  const expires = new Date();
  expires.setTime(expires.getTime() + expirationDays * 24 * 60 * 60 * 1000);
  let instructions = key + "=" + value + "; expires=" + expires.toUTCString() + "; path=/; domain=" + domain;
  document.cookie = instructions;
};

// Wire shape for any encounter handed to the native offline queue. The app
// stores the object verbatim as its "diff" and replays it straight through
// api.updateChat on reconnect (see usePostMessages.js syncOfflineData), and that
// endpoint's assignedTo trigger param is declared "type": "string" server-side
// (Note - Update.json) -- it is passed as-is into getChatDatabase().updateChat,
// the same single userId string reassignEncounter and ensureAssignedToMe's
// online branch both send. Local/UI state instead holds assignedTo as an ARRAY
// (chat.assignedTo[0] is read throughout, including isToReassign()), which is
// why the outgoing copy -- and only the outgoing copy, never the local record --
// is normalized here. The value itself is the chart's real owner: the offline
// gate does not stamp ownership locally, and the replay deletes this key before
// it reaches the server, so nothing here can transfer an encounter.
export const normalizeAssignedToForNative = (chat) => {
  if (!chat || !Array.isArray(chat.assignedTo)) return chat;
  return { ...chat, assignedTo: chat.assignedTo[0] };
};

// Utils
export const sendToMobileApp = (data) => {
  try {
    if (!window.ReactNativeWebView?.postMessage) return false;
    let message = data;
    if (data?.type === "recordingUpdate") {
      const recordingId = resolveNativeRecordingUpdateId(data.recordingId);
      // An uncorrelated UUID cannot safely identify a native database row.
      // Keep its audio retryable instead of letting native parse a UUID prefix.
      if (recordingId === undefined) return false;
      message = { ...data, recordingId };
    }
    window.ReactNativeWebView.postMessage(JSON.stringify(message));
    return true;
  } catch (e) {
    console.error("Error sending message to mobile app");
    console.error(e);
    return false;
  }
};

export const PDF_PASSWORD_PROTECTED = "PDF_PASSWORD_PROTECTED";

const isEncryptedPdfError = (e) => /encrypted/i.test(e?.message || "");

// pdf-lib can't decrypt, so encrypted donors (typically EHR-exported facesheets
// with an empty user password) are rendered to images via pdf.js — which CAN
// decrypt them — and embedded page-by-page. Throws { code: PDF_PASSWORD_PROTECTED }
// when the PDF needs a real password to open.
const rasterizePdfIntoDoc = async (pdfDoc, base64) => {
  const { getDocument, GlobalWorkerOptions } = await import("pdfjs-dist");
  GlobalWorkerOptions.workerSrc = resolvePublicAssetUrl("pdf.worker.min.mjs") + "?v=5.4.149";

  const raw = atob(base64.split(",").pop());
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);

  let pdf;
  try {
    pdf = await getDocument({ wasmUrl: resolvePublicAssetUrl("wasm/"), data: bytes }).promise;
  } catch (e) {
    if (e?.name === "PasswordException") {
      const err = new Error("PDF requires a password to open");
      err.code = PDF_PASSWORD_PROTECTED;
      throw err;
    }
    throw e;
  }

  try {
    for (let p = 1; p <= pdf.numPages; p++) {
      const page = await pdf.getPage(p);
      const viewport = page.getViewport({ scale: 2 });
      const canvas = document.createElement("canvas");
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;

      const img = await pdfDoc.embedJpg(canvas.toDataURL("image/jpeg", 0.92));
      const pageSize = page.getViewport({ scale: 1 });
      const newPage = pdfDoc.addPage([pageSize.width, pageSize.height]);
      newPage.drawImage(img, { x: 0, y: 0, width: pageSize.width, height: pageSize.height });
    }
  } finally {
    pdf.destroy();
  }
};

// Single-PDF uploads bypass mergeDocs, but the backend's AI extraction
// (Claude process-document) rejects password/encryption-flagged PDFs, so an
// encrypted facesheet must still be rebuilt client-side. Non-encrypted PDFs
// pass through untouched to preserve the original text layer.
export const normalizePdfForUpload = async (base64) => {
  try {
    await PDFDocument.load(base64);
    return base64;
  } catch (e) {
    if (!isEncryptedPdfError(e)) return base64;
    const pdfDoc = await PDFDocument.create();
    await rasterizePdfIntoDoc(pdfDoc, base64);
    return await pdfDoc.saveAsBase64({ dataUri: true });
  }
};

export const mergeDocs = async (uploads) => {
  const pdfDoc = await PDFDocument.create();

  // iterate over all documents to merge
  for (let u of uploads) {
    let extension = u.filename.split(".").pop();
    let rotate = 0;

    if (extension === "pdf") {
      let donorPdfDoc;
      try {
        donorPdfDoc = await PDFDocument.load(u.base64);
      } catch (e) {
        if (!isEncryptedPdfError(e)) throw e;
        await rasterizePdfIntoDoc(pdfDoc, u.base64);
        continue;
      }
      const docLength = donorPdfDoc.getPageCount();

      for (var k = 0; k < docLength; k++) {
        const [donorPage] = await pdfDoc.copyPages(donorPdfDoc, [k]);
        pdfDoc.addPage(donorPage);
      }
    } else {
      const page = pdfDoc.addPage();
      let img;

      let base64 = u.base64;

      try {
        let fileReader = await arrayBufferToFileReader(u.file);
        let tags = u.type === "image/heif" ? window.findEXIFinHEIC(fileReader.result) : window.findEXIFinJPEG(fileReader.result);

        if (tags && tags.Orientation) {
          // Rotate img
          if (tags.Orientation === 3) {
            rotate = 180;
          } else if (tags.Orientation === 5 || tags.Orientation === 6) {
            rotate = 90;
          } else if (tags.Orientation === 7 || tags.Orientation === 8) {
            rotate = -90;
          }
        }

        if (rotate) {
          base64 = await rotateImg(u.base64, rotate, u.type);
        }
      } catch (e) {
        console.warn(e);
      }

      if (u.type === "image/heic" || u.type === "image/heif") {
        const fileJpeg = await heicTo({
          blob: u.file,
          type: "image/jpeg",
          quality: 1,
        });
        let base64Jpg = await convertFileToBase64(fileJpeg);
        img = await pdfDoc.embedJpg(base64Jpg);
      } else if (u.type === "image/jpeg") {
        img = await pdfDoc.embedJpg(base64);
      } else {
        img = await pdfDoc.embedPng(base64);
      }

      const jpgDims = img.scaleToFit(page.getWidth(), page.getHeight());

      // Draw image on doc
      page.drawImage(img, {
        x: 0,
        y: page.getHeight() - jpgDims.height,
        width: jpgDims.width,
        height: jpgDims.height,
      });
    }
  }

  // save as a Base64 URI
  const pdfDataUri = await pdfDoc.saveAsBase64({ dataUri: true });
  return pdfDataUri;
};

function rotateImg(base64, degrees, type) {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      // Calculate new dimensions, considering rotation
      let width = img.width;
      let height = img.height;

      if (degrees === 90 || degrees === 270) {
        width = img.height;
        height = img.width;
      }

      canvas.width = width;
      canvas.height = height;

      // Translate and rotate the canvas
      ctx.translate(width / 2, height / 2);
      ctx.rotate((degrees * Math.PI) / 180);
      ctx.translate(-img.width / 2, -img.height / 2);

      // Draw the image
      ctx.drawImage(img, 0, 0);

      // Get the new base64 data URL
      const rotatedBase64 = canvas.toDataURL(type);
      resolve(rotatedBase64);
    };

    img.onerror = (error) => {
      reject(error);
    };

    img.src = base64;
  });
}

export const downloadPdf = async (pdfBytes) => {
  const blob = new Blob([pdfBytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "1500 Form.pdf";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const generatePdf = async (chat, mapFields) => {
  const template = await fetch("https://" + window.location.hostname + "/template.pdf").then((res) => res.arrayBuffer());
  const pdfDoc = await PDFDocument.load(template);
  const form = pdfDoc.getForm();

  // const fields = form.getFields();
  // for (const field of fields) {
  //   if (field.constructor.name === "PDFTextField") {
  //     field.setText(field.getName().substring(0, field.getMaxLength() || field.getName().length));
  //   } else if (field.constructor.name === "PDFCheckBox") {
  //     const widgets = field.acroField.getWidgets();
  //     // Set the first widget as checked
  //     let checkIndex = 2;
  //     const state = widgets[checkIndex]?.getOnValue();
  //     if (state) {
  //       widgets[checkIndex].setAppearanceState(state);
  //     }
  //   }
  // }

  mapFields(chat, form, setValue);

  // pdf-lib's setText writes the value into the form-field dictionary but does
  // NOT regenerate the visible appearance stream unless we tell it to. Without
  // this call, PDF.js (and most viewers) only paint fields that had a pre-baked
  // appearance from the template designer — which is why filled-in claim forms
  // historically rendered mostly blank in our viewer.
  const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
  form.updateFieldAppearances(helveticaFont);

  const pdfBytes = await pdfDoc.save();
  return { pdfBytes, pdfForm: form };
};

const setValue = (form, name, value) => {
  if (value !== null && value !== undefined) {
    try {
      let field = form.getField(name);
      if (field.setText) {
        // Set text
        field.setText(value?.toString());
      } else if (field.check) {
        // Set checkbox
        const widgets = field.acroField.getWidgets();
        // const state = widgets[value]?.getOnValue();
        const index = widgets.findIndex((w) => {
          return w.getOnValue().encodedName === "/" + value;
        });
        if (index !== -1) {
          let state = widgets[index]?.getOnValue();
          widgets[index].setAppearanceState(state);
        }
      } else {
        console.warn("Field type not managed for ", name, field.constructor.name);
      }
    } catch (e) {
      console.warn("Field not found", name, e);
    }
  }
};

// Converts a doctor type string (e.g., kebab-case, snake_case, or camelCase) to camelCase for consistent internal use.
export function normalizeDoctorType(id) {
  if (!id) return "";
  return id.replace(/[-_](.)/g, (_, char) => char.toUpperCase()).replace(/^(.)/, (m) => m.toLowerCase());
}

export function formatCommissionLabel({ commissionType = "flat", commission = 150, commissionPercent = 0 }) {
  const flat = `$${commission ?? 0}/order`;
  const pct = `${commissionPercent ?? 0}%`;

  switch (commissionType) {
    case "percent":
      return pct;
    case "lesser":
      return `${flat} / ${pct} (lesser)`;
    case "flat":
    default:
      return flat;
  }
}

// PDF Fields

// NUCC USE PDFTextField
// 40 PDFTextField
// 57 PDFTextField
// 58 PDFTextField
// 41 PDFTextField
// 50 PDFTextField
// 73 PDFTextField
// 74 PDFTextField
// 85 PDFTextField
// 96 PDFTextField
// 99icd PDFTextField
// 135 PDFTextField
// 157 PDFTextField
// 179 PDFTextField
// 201 PDFTextField
// 223 PDFTextField
// 245 PDFTextField
// 276 PDFCheckBox
// Clear Form PDFButton
// insurance_name PDFTextField
// insurance_address PDFTextField
// insurance_address2 PDFTextField
// insurance_city_state_zip PDFTextField
// pt_name PDFTextField
// insurance_id PDFTextField
// ins_name PDFTextField
// insurance_type PDFCheckBox
// birth_mm PDFTextField
// birth_dd PDFTextField
// birth_yy PDFTextField
// sex PDFCheckBox
// pt_street PDFTextField
// pt_city PDFTextField
// pt_state PDFTextField
// pt_zip PDFTextField
// pt_AreaCode PDFTextField
// pt_phone PDFTextField
// rel_to_ins PDFCheckBox
// ins_street PDFTextField
// ins_city PDFTextField
// ins_state PDFTextField
// ins_zip PDFTextField
// ins_phone area PDFTextField
// ins_phone PDFTextField
// ins_policy PDFTextField
// ins_dob_mm PDFTextField
// ins_dob_dd PDFTextField
// ins_dob_yy PDFTextField
// ins_sex PDFCheckBox
// other_ins_name PDFTextField
// other_ins_policy PDFTextField
// ins_signature PDFTextField
// ins_benefit_plan PDFCheckBox
// ins_plan_name PDFTextField
// pt_signature PDFTextField
// pt_date PDFTextField
// cur_ill_mm PDFTextField
// cur_ill_dd PDFTextField
// cur_ill_yy PDFTextField
// ref_physician PDFTextField
// id_physician PDFTextField
// physician number 17a1 PDFTextField
// physician number 17a PDFTextField
// sim_ill_mm PDFTextField
// sim_ill_dd PDFTextField
// sim_ill_yy PDFTextField
// work_mm_from PDFTextField
// work_dd_from PDFTextField
// work_yy_from PDFTextField
// work_mm_end PDFTextField
// work_dd_end PDFTextField
// work_yy_end PDFTextField
// hosp_mm_from PDFTextField
// hosp_dd_from PDFTextField
// hosp_yy_from PDFTextField
// hosp_mm_end PDFTextField
// hosp_dd_end PDFTextField
// hosp_yy_end PDFTextField
// lab PDFCheckBox
// charge PDFTextField
// medicaid_resub PDFTextField
// original_ref PDFTextField
// prior_auth PDFTextField
// emg1 PDFTextField
// local1a PDFTextField
// sv1_mm_from PDFTextField
// sv1_dd_from PDFTextField
// sv1_yy_from PDFTextField
// sv1_mm_end PDFTextField
// sv1_dd_end PDFTextField
// sv1_yy_end PDFTextField
// diag1 PDFTextField
// ch1 PDFTextField
// day1 PDFTextField
// sv2_mm_from PDFTextField
// sv3_mm_from PDFTextField
// sv4_mm_from PDFTextField
// sv5_mm_from PDFTextField
// sv6_mm_from PDFTextField
// sv2_dd_from PDFTextField
// sv3_dd_from PDFTextField
// sv4_dd_from PDFTextField
// sv5_dd_from PDFTextField
// sv6_dd_from PDFTextField
// sv2_yy_from PDFTextField
// sv3_yy_from PDFTextField
// sv4_yy_from PDFTextField
// sv5_yy_from PDFTextField
// sv6_yy_from PDFTextField
// sv2_mm_end PDFTextField
// sv3_mm_end PDFTextField
// sv4_mm_end PDFTextField
// sv5_mm_end PDFTextField
// sv6_mm_end PDFTextField
// sv2_dd_end PDFTextField
// sv3_dd_end PDFTextField
// sv4_dd_end PDFTextField
// sv5_dd_end PDFTextField
// sv6_dd_end PDFTextField
// sv2_yy_end PDFTextField
// sv3_yy_end PDFTextField
// sv4_yy_end PDFTextField
// sv5_yy_end PDFTextField
// sv6_yy_end PDFTextField
// place2 PDFTextField
// place3 PDFTextField
// place4 PDFTextField
// place5 PDFTextField
// place6 PDFTextField
// diag2 PDFTextField
// diag3 PDFTextField
// diag4 PDFTextField
// ch2 PDFTextField
// ch3 PDFTextField
// ch4 PDFTextField
// ch5 PDFTextField
// ch6 PDFTextField
// Suppla PDFTextField
// Supplb PDFTextField
// Supplc PDFTextField
// Suppld PDFTextField
// Supple PDFTextField
// day2 PDFTextField
// day3 PDFTextField
// day4 PDFTextField
// day5 PDFTextField
// day6 PDFTextField
// emg2 PDFTextField
// emg3 PDFTextField
// emg4 PDFTextField
// emg5 PDFTextField
// emg6 PDFTextField
// local1 PDFTextField
// local2a PDFTextField
// local2 PDFTextField
// local3a PDFTextField
// local3 PDFTextField
// local4a PDFTextField
// local4 PDFTextField
// local5a PDFTextField
// local5 PDFTextField
// local6a PDFTextField
// local6 PDFTextField
// tax_id PDFTextField
// pt_account PDFTextField
// assignment PDFCheckBox
// t_charge PDFTextField
// amt_paid PDFTextField
// physician_signature PDFTextField
// physician_date PDFTextField
// fac_name PDFTextField
// fac_street PDFTextField
// fac_location PDFTextField
// doc_name PDFTextField
// doc_street PDFTextField
// doc_location PDFTextField
// doc_phone area PDFTextField
// doc_phone PDFTextField
// pin PDFTextField
// grp PDFTextField
// employment PDFCheckBox
// pt_auto_accident PDFCheckBox
// accident_place PDFTextField
// other_accident PDFCheckBox
// other_ins_plan_name PDFTextField
// diagnosis1 PDFTextField
// diagnosis4 PDFTextField
// diagnosis3 PDFTextField
// diagnosis2 PDFTextField
// diagnosis5 PDFTextField
// diagnosis6 PDFTextField
// diagnosis7 PDFTextField
// diagnosis8 PDFTextField
// diagnosis9 PDFTextField
// diagnosis10 PDFTextField
// diagnosis11 PDFTextField
// diagnosis12 PDFTextField
// ssn PDFCheckBox
// place1 PDFTextField
// diag6 PDFTextField
// diag5 PDFTextField
// type6 PDFTextField
// type5 PDFTextField
// cpt6 PDFTextField
// cpt5 PDFTextField
// cpt4 PDFTextField
// mod6 PDFTextField
// mod5 PDFTextField
// mod4 PDFTextField
// mod3 PDFTextField
// mod2 PDFTextField
// mod6a PDFTextField
// mod5a PDFTextField
// mod4a PDFTextField
// mod3a PDFTextField
// mod2a PDFTextField
// mod1a PDFTextField
// mod6b PDFTextField
// mod5b PDFTextField
// mod4b PDFTextField
// mod3b PDFTextField
// mod2b PDFTextField
// mod1b PDFTextField
// mod6c PDFTextField
// mod5c PDFTextField
// mod4c PDFTextField
// mod3c PDFTextField
// mod1c PDFTextField
// mod2c PDFTextField
// type1 PDFTextField
// type2 PDFTextField
// type3 PDFTextField
// type4 PDFTextField
// cpt1 PDFTextField
// cpt2 PDFTextField
// cpt3 PDFTextField
// mod1 PDFTextField
// pin1 PDFTextField
// grp1 PDFTextField
// Suppl PDFTextField
// plan1 PDFTextField
// plan2 PDFTextField
// plan3 PDFTextField
// plan4 PDFTextField
// plan5 PDFTextField
// plan6 PDFTextField
// epsdt1 PDFTextField
// epsdt3 PDFTextField
// epsdt6 PDFTextField
// epsdt5 PDFTextField
// epsdt4 PDFTextField
// epsdt2 PDFTextField
